import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity, Database, Clock, Users, HardDrive, Wifi, WifiOff,
  RefreshCcw, Loader2, CheckCircle, AlertTriangle, XCircle,
  Shield, Zap, BarChart3, Server, Globe, Bot
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface HealthCheck {
  name: string;
  status: 'ok' | 'warning' | 'error' | 'loading';
  responseTime?: number;
  detail?: string;
  icon: React.ReactNode;
}

interface SystemMetrics {
  totalUsers: number;
  activeUsersToday: number;
  totalQuestions: number;
  totalConversations: number;
  totalMessages: number;
  totalFeedback: number;
  totalAiCalls: number;
  maintenanceMode: boolean;
}

export const SystemStatusPage: React.FC = () => {
  const [checks, setChecks] = useState<HealthCheck[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [maintenanceToggling, setMaintenanceToggling] = useState(false);

  const runHealthChecks = useCallback(async () => {
    const results: HealthCheck[] = [];

    // 1. Database connectivity
    const dbCheck: HealthCheck = { name: 'Datenbank', status: 'loading', icon: <Database size={18} /> };
    try {
      const start = performance.now();
      const { error } = await adminDb.from('users').select('id', { count: 'exact', head: true });
      const time = Math.round(performance.now() - start);
      dbCheck.responseTime = time;
      dbCheck.status = error ? 'error' : time > 2000 ? 'warning' : 'ok';
      dbCheck.detail = error ? error.message : `${time}ms Antwortzeit`;
    } catch (e: any) {
      dbCheck.status = 'error';
      dbCheck.detail = e.message;
    }
    results.push(dbCheck);

    // 2. Auth service
    const authCheck: HealthCheck = { name: 'Auth-Service', status: 'loading', icon: <Shield size={18} /> };
    try {
      const start = performance.now();
      const { error } = await supabase.auth.getSession();
      const time = Math.round(performance.now() - start);
      authCheck.responseTime = time;
      authCheck.status = error ? 'error' : time > 2000 ? 'warning' : 'ok';
      authCheck.detail = error ? error.message : `${time}ms Antwortzeit`;
    } catch (e: any) {
      authCheck.status = 'error';
      authCheck.detail = e.message;
    }
    results.push(authCheck);

    // 3. Storage
    const storageCheck: HealthCheck = { name: 'Storage', status: 'loading', icon: <HardDrive size={18} /> };
    try {
      const start = performance.now();
      const { error } = await supabase.storage.listBuckets();
      const time = Math.round(performance.now() - start);
      storageCheck.responseTime = time;
      storageCheck.status = error ? 'error' : time > 3000 ? 'warning' : 'ok';
      storageCheck.detail = error ? error.message : `${time}ms Antwortzeit`;
    } catch (e: any) {
      storageCheck.status = 'error';
      storageCheck.detail = e.message;
    }
    results.push(storageCheck);

    // 4. Edge Functions (via a lightweight ping if available)
    const edgeCheck: HealthCheck = { name: 'Edge Functions', status: 'loading', icon: <Zap size={18} /> };
    try {
      const start = performance.now();
      // Just check if we can reach Supabase API
      const { error } = await adminDb.from('feature_flags').select('key', { count: 'exact', head: true });
      const time = Math.round(performance.now() - start);
      edgeCheck.responseTime = time;
      edgeCheck.status = error ? 'warning' : 'ok';
      edgeCheck.detail = error ? 'Nicht erreichbar' : `API erreichbar (${time}ms)`;
    } catch {
      edgeCheck.status = 'warning';
      edgeCheck.detail = 'Nicht konfiguriert';
    }
    results.push(edgeCheck);

    // 5. Realtime
    const realtimeCheck: HealthCheck = { name: 'Realtime', status: 'loading', icon: <Wifi size={18} /> };
    try {
      realtimeCheck.status = navigator.onLine ? 'ok' : 'error';
      realtimeCheck.detail = navigator.onLine ? 'WebSocket verbunden' : 'Offline';
    } catch {
      realtimeCheck.status = 'warning';
      realtimeCheck.detail = 'Status unbekannt';
    }
    results.push(realtimeCheck);

    setChecks(results);
    setLastRefresh(new Date());
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const [usersRes, questionsRes, convoRes, messagesRes, feedbackRes, aiRes, flagRes] = await Promise.all([
        adminDb.from('users').select('id, data, updated_at'),
        adminDb.from('questions').select('id', { count: 'exact', head: true }),
        adminDb.from('coach_conversations').select('id', { count: 'exact', head: true }),
        adminDb.from('coach_messages').select('id', { count: 'exact', head: true }),
        adminDb.from('beta_feedback').select('id', { count: 'exact', head: true }),
        adminDb.from('ai_usage_logs').select('id', { count: 'exact', head: true }),
        adminDb.from('feature_flags').select('enabled').eq('key', 'maintenance_mode').single(),
      ]);

      const today = new Date().toISOString().split('T')[0];
      const activeToday = (usersRes.data || []).filter(u => {
        const lastLogin = (u.data as any)?.lastLoginDate || u.updated_at;
        return lastLogin && lastLogin.startsWith(today);
      }).length;

      setMetrics({
        totalUsers: usersRes.data?.length || 0,
        activeUsersToday: activeToday,
        totalQuestions: questionsRes.count || 0,
        totalConversations: convoRes.count || 0,
        totalMessages: messagesRes.count || 0,
        totalFeedback: feedbackRes.count || 0,
        totalAiCalls: aiRes.count || 0,
        maintenanceMode: flagRes.data?.enabled || false,
      });
    } catch (e) {
      console.error('Metrics load failed:', e);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([runHealthChecks(), loadMetrics()]);
    setLoading(false);
  }, [runHealthChecks, loadMetrics]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Auto-Refresh every 15s
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshAll, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  const toggleMaintenance = async () => {
    if (!metrics) return;
    const newVal = !metrics.maintenanceMode;
    if (newVal && !confirm('Wartungsmodus aktivieren? Alle Nutzer werden von der App ausgesperrt.')) return;
    setMaintenanceToggling(true);
    try {
      const session = await supabase.auth.getSession();
      const email = session.data.session?.user?.email || 'unknown';

      await supabase
        .from('feature_flags')
        .update({ enabled: newVal, updated_by: email, updated_at: new Date().toISOString() })
        .eq('key', 'maintenance_mode');

      await adminDb.from('audit_logs').insert({
        admin_email: email,
        action: 'maintenance_mode.toggle',
        resource_type: 'feature_flag',
        resource_id: 'maintenance_mode',
        old_value: { enabled: !newVal },
        new_value: { enabled: newVal },
      });

      setMetrics({ ...metrics, maintenanceMode: newVal });
    } catch (err: any) {
      console.error(err);
    } finally {
      setMaintenanceToggling(false);
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case 'ok': return 'var(--success)';
      case 'warning': return '#ca8a04';
      case 'error': return '#dc2626';
      default: return 'var(--text-muted)';
    }
  };

  const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
    switch (status) {
      case 'ok': return <CheckCircle size={16} style={{ color: 'var(--success)' }} />;
      case 'warning': return <AlertTriangle size={16} style={{ color: '#ca8a04' }} />;
      case 'error': return <XCircle size={16} style={{ color: '#dc2626' }} />;
      default: return <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />;
    }
  };

  const allOk = checks.length > 0 && checks.every(c => c.status === 'ok');
  const hasWarnings = checks.some(c => c.status === 'warning');
  const hasErrors = checks.some(c => c.status === 'error');

  return (
    <div>
      <div className="page-header">
        <h1><Activity size={24} /> System-Status</h1>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-xs text-muted" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-Refresh (15s)
          </label>
          <button className="btn btn-secondary btn-sm" onClick={refreshAll} disabled={loading}>
            <RefreshCcw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className="card mb-4" style={{
        borderLeft: `4px solid ${hasErrors ? '#dc2626' : hasWarnings ? '#ca8a04' : 'var(--success)'}`,
        display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px',
      }}>
        {allOk ? <CheckCircle size={28} style={{ color: 'var(--success)' }} /> :
         hasErrors ? <XCircle size={28} style={{ color: '#dc2626' }} /> :
         <AlertTriangle size={28} style={{ color: '#ca8a04' }} />}
        <div>
          <h3 style={{ margin: 0 }}>
            {allOk ? 'Alle Systeme laufen normal ✅' : hasErrors ? 'Probleme erkannt ⚠️' : 'Eingeschränkt verfügbar ⚡'}
          </h3>
          <span className="text-sm text-muted">
            Letzte Prüfung: {lastRefresh.toLocaleTimeString('de-DE')}
          </span>
        </div>
      </div>

      {/* Maintenance Mode Toggle */}
      <div className="card mb-4" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 24px',
        borderLeft: `4px solid ${metrics?.maintenanceMode ? '#dc2626' : 'var(--border)'}`,
        background: metrics?.maintenanceMode ? 'var(--danger-bg, #fef2f2)' : undefined,
      }}>
        <div className="flex items-center gap-3">
          <Shield size={20} style={{ color: metrics?.maintenanceMode ? '#dc2626' : 'var(--text-muted)' }} />
          <div>
            <h4 style={{ margin: 0 }}>Wartungsmodus</h4>
            <p className="text-sm text-muted" style={{ margin: 0 }}>
              {metrics?.maintenanceMode
                ? 'AKTIV — Nutzer sehen eine Wartungsseite'
                : 'Deaktiviert — App ist normal erreichbar'}
            </p>
          </div>
        </div>
        <button
          onClick={toggleMaintenance}
          disabled={maintenanceToggling}
          style={{
            width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
            background: metrics?.maintenanceMode ? '#dc2626' : 'var(--border)',
            position: 'relative', transition: 'background 0.2s',
            opacity: maintenanceToggling ? 0.5 : 1,
          }}
        >
          <div style={{
            width: 22, height: 22, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 3,
            left: metrics?.maintenanceMode ? 27 : 3,
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Health Checks */}
        <div className="card">
          <h3 className="flex items-center gap-2 mb-4"><Server size={18} /> Health Checks</h3>
          <div className="flex flex-col gap-2">
            {checks.map(check => (
              <div key={check.name} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: 'var(--background)', borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
              }}>
                <div className="flex items-center gap-3">
                  <div style={{ color: statusColor(check.status) }}>{check.icon}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{check.name}</div>
                    <div className="text-xs text-muted">{check.detail}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {check.responseTime !== undefined && (
                    <span className="text-xs text-muted" style={{
                      background: 'var(--border-light)', padding: '2px 8px', borderRadius: 8
                    }}>{check.responseTime}ms</span>
                  )}
                  <StatusIcon status={check.status} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Metrics */}
        <div className="card">
          <h3 className="flex items-center gap-2 mb-4"><BarChart3 size={18} /> Live-Metriken</h3>
          {metrics ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <MetricCard icon={<Users size={16} />} label="Nutzer gesamt" value={metrics.totalUsers} />
              <MetricCard icon={<Activity size={16} />} label="Aktiv heute" value={metrics.activeUsersToday} color="#16a34a" />
              <MetricCard icon={<Database size={16} />} label="Fragen" value={metrics.totalQuestions} />
              <MetricCard icon={<Bot size={16} />} label="KI-Aufrufe" value={metrics.totalAiCalls} color="#7c3aed" />
              <MetricCard icon={<Globe size={16} />} label="Coach-Chats" value={metrics.totalConversations} />
              <MetricCard icon={<Zap size={16} />} label="Nachrichten" value={metrics.totalMessages} />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: number; color?: string }> = ({ icon, label, value, color }) => (
  <div style={{
    padding: '14px 16px', background: 'var(--background)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-light)',
  }}>
    <div className="flex items-center gap-2 text-muted" style={{ fontSize: 11, marginBottom: 4 }}>
      {icon} {label}
    </div>
    <div style={{ fontWeight: 700, fontSize: 22, color: color || 'var(--text)' }}>
      {value.toLocaleString()}
    </div>
  </div>
);
